import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Text from '@components/common/text';
import { usePassReviewPostComments, useUpdatePassReviewPostComments } from '@repository/passReview/usePassReview';
import { PassReviewCommentListDto } from '@domain/rs/passReview/PassReviewCommentListGetRs';
import { FontStyle } from '@domain/constant/fontStyle';
import Colors from '@domain/constant/colors';
import DateFormat from '@utils/date/format';
import classnames from 'classnames/bind';
import styles from './index.module.scss';
const cx = classnames.bind(styles);

interface ItemProps {
  data: PassReviewCommentListDto;
  openCommentDeleteModal: (sn: number) => void;
}

const Item = ({ data, openCommentDeleteModal }: ItemProps) => {
  const { query } = useRouter();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [myComment, setMyComment] = useState(data.comment);
  const [editView, setEditView] = useState(false);
  const { refetch } = usePassReviewPostComments(Number(query.sn), { enabled: false });
  const { mutateAsync: updateComment } = useUpdatePassReviewPostComments();

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    handleResizeHeight();
    setMyComment(event.target.value);
  };

  const handleResizeHeight = () => {
    if (!ref.current) return;

    const target = ref.current;
    target.style.height = '0';
    target.style.height = `${target.scrollHeight}px`;
  };

  const updateBtn = async () => {
    await updateComment(
      { sn: Number(query.sn), commentSn: data.commentSn, comment: myComment },
      {
        onSuccess: () => {
          refetch();
          setEditView(false);
        },
      },
    );
  };

  useEffect(() => {
    if (!editView || !ref.current) return;
    ref.current?.focus();
    handleResizeHeight();
    ref.current.setSelectionRange(ref.current.value.length, ref.current.value.length);
  }, [editView]);

  return (
    <div className={cx('item', { my: data.userComment })}>
      <div className={cx('infoArea')}>
        <div className={cx('info')}>
          <Text fontStyle={FontStyle.B3P_B}>{data.commentUserId}</Text>
          <div className={cx('divider')} />
          <Text fontStyle={FontStyle.B3P_M} color={Colors.C_COOL_GRAY_60}>
            {DateFormat.format(new Date(data.createdDateTime), 'yyyy.MM.dd')}
          </Text>
        </div>
        {!editView && data.userComment && (
          <div className={cx('info')}>
            <button
              className={cx('btn')}
              onClick={() => {
                setEditView(true);
              }}
            >
              수정
            </button>
            <div className={cx('divider')} />
            <button className={cx('btn')} onClick={() => openCommentDeleteModal(data.commentSn)}>
              삭제
            </button>
          </div>
        )}
      </div>

      {!editView ? (
        <Text
          fontStyle={FontStyle.B2P_M}
          color={Colors.C_COOL_GRAY_80}
          className={cx('comment')}
          styles={{ whiteSpace: 'pre-wrap' }}
        >
          {data.comment}
        </Text>
      ) : (
        <div className={cx('commentArea')}>
          <div style={{ height: ref.current?.scrollHeight, minHeight: 98 }}>
            <textarea ref={ref} maxLength={300} value={myComment} onChange={onChange} style={{ minHeight: 98 }} />
          </div>

          <div className={cx('register')}>
            <Text
              fontStyle={FontStyle.B4_M}
              color={Colors.C_COOL_GRAY_70}
            >{`최대 글자수 : ${myComment.length} / 300`}</Text>
            <div>
              <button className={cx('btn', 'cancle')} onClick={() => setEditView(false)}>
                취소
              </button>
              <button className={cx('btn')} disabled={!myComment.length} onClick={updateBtn}>
                수정
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Item;
